import { ArrowRight, ChevronRight } from 'lucide-react';
import { Link, useParams } from 'react-router';

import { Button, Card, CardContent, Skeleton } from '@workspace/shadcn';

import { useInstructor } from '../../lib/api.ts';
import { usePageMeta } from '../../lib/seo.tsx';
import { Section } from '../components/section.tsx';
import NotFound from './not-found.tsx';

export default function InstructorDetail() {
  const { slug = '' } = useParams();
  const { data: instructor, isLoading, isError } = useInstructor(slug);

  usePageMeta({
    title: instructor
      ? `ITGL Academy | ${instructor.name}`
      : 'ITGL Academy | Instructor',
    description:
      instructor?.bio?.split('.')[0] ??
      'Meet the expert instructors behind our healthcare courses.',
    pathname: `/instructors/${slug}`,
  });

  if (isError) return <NotFound />;

  if (isLoading || !instructor) {
    return (
      <Section className="mt-16">
        <Skeleton className="mx-auto mb-6 size-44 rounded-full" />
        <Skeleton className="mx-auto h-64 w-full max-w-4xl rounded-lg" />
      </Section>
    );
  }

  const paragraphs = instructor.bio
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return (
    <Section className="relative mt-16 md:mt-4 lg:pb-4">
      <div className="mx-auto max-w-4xl">
        <Card className="mb-12 overflow-hidden">
          <CardContent className="space-y-4 p-6 text-center sm:p-8">
            <div className="border-background mx-auto size-44 overflow-hidden rounded-full border-4 shadow-lg">
              <img
                loading="eager"
                src={instructor.imageUrl}
                alt={instructor.name}
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold sm:text-4xl">
                {instructor.name}
              </h1>
              <p className="bg-primary text-background mx-auto my-2 w-fit rounded-md px-2 py-1 text-sm">
                {instructor.position}
              </p>
              <p className="text-muted-foreground text-sm">
                {instructor.institution}
              </p>
            </div>
          </CardContent>
        </Card>

        {paragraphs.length > 0 && (
          <div className="mb-12">
            <h2 className="mb-4 text-2xl font-semibold">Biography</h2>
            <Card>
              <CardContent className="text-muted-foreground space-y-4 p-6">
                {paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {instructor.courses.length > 0 && (
          <div>
            <h2 className="mb-4 text-2xl font-semibold">Courses</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {instructor.courses
                .filter((course) => !course.comingSoon)
                .map((course) => (
                  <Link key={course.slug} to={`/courses/${course.slug}`}>
                    <Card className="group hover:bg-primary/5 h-full cursor-pointer transition-colors">
                      <CardContent className="flex items-center justify-between gap-2 p-4">
                        <span className="group-hover:text-primary font-medium transition-colors">
                          {course.title}
                        </span>
                        <ChevronRight className="text-muted-foreground group-hover:text-primary h-5 w-5 shrink-0 transition-colors" />
                      </CardContent>
                    </Card>
                  </Link>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="my-12 flex flex-col items-center justify-center">
        <div className="border-muted mb-6 h-px w-full border-t border-dashed" />
        <Link to="/courses">
          <Button className="group flex h-12 items-center gap-1 px-8 text-base">
            View All Courses
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </Link>
      </div>
    </Section>
  );
}
